/**
 * kmathQuestions — fresh procedurally-generated questions for Kiddom Math activities.
 *
 * Every call returns a NEW set of questions randomized off `content.skill` + `content.grade`,
 * so kids don't see the same 3 problems every time they reopen a lesson.
 *
 * Question shape matches the rest of ActivityPage:
 *   { question, options: string[], answer: string, hint: string }
 */

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const buildOptions = (correct, distractors) => {
  const uniq = Array.from(new Set([correct, ...distractors].map(String)));
  const needed = 4 - uniq.length;
  for (let i = 0; i < needed; i++) uniq.push(String(Number(correct) + (i + 5)));
  return shuffle(uniq.slice(0, 4));
};

const makeAdd = (max) => {
  const a = randInt(0, max);
  const b = randInt(0, max - a);
  const ans = a + b;
  return {
    question: `${a} + ${b} = ?`,
    options: buildOptions(ans, [ans + 1, ans - 1, ans + 2, ans - 2].filter((n) => n >= 0)),
    answer: String(ans),
    hint: `Start at ${a} and count up ${b}.`,
  };
};

const makeSub = (max) => {
  const a = randInt(1, max);
  const b = randInt(0, a);
  const ans = a - b;
  return {
    question: `${a} − ${b} = ?`,
    options: buildOptions(ans, [ans + 1, ans - 1, ans + 2, ans - 2].filter((n) => n >= 0)),
    answer: String(ans),
    hint: `Start at ${a} and take away ${b}.`,
  };
};

const makeCountingNext = (max) => {
  const n = randInt(0, max - 1);
  const ans = n + 1;
  return {
    question: `What number comes after ${n}?`,
    options: buildOptions(ans, [ans + 1, ans - 1, ans + 2].filter((x) => x >= 0)),
    answer: String(ans),
    hint: 'Count up by one.',
  };
};

const makeCountObjects = () => {
  const n = randInt(1, 10);
  const emoji = ['🍎', '⭐', '🐝', '🎈', '🦋', '🐟'][randInt(0, 5)];
  return {
    question: `How many ${emoji}?  ${emoji.repeat(n)}`,
    options: buildOptions(n, [n + 1, n - 1, n + 2].filter((x) => x >= 0)),
    answer: String(n),
    hint: 'Point to each one and count out loud.',
  };
};

const makeCompare = (max) => {
  const a = randInt(1, max);
  let b = randInt(1, max);
  while (b === a) b = randInt(1, max);
  const ans = String(Math.max(a, b));
  return {
    question: `Which is greater: ${a} or ${b}?`,
    options: [String(a), String(b)],
    answer: ans,
    hint: 'The bigger number is farther along the number line.',
  };
};

const makeSkipCount = (step) => {
  const start = randInt(0, 5) * step;
  const seq = [start, start + step, start + 2 * step];
  const ans = start + 3 * step;
  return {
    question: `Skip count by ${step}:  ${seq.join(', ')}, ?`,
    options: buildOptions(ans, [ans + 1, ans - step, ans + step]),
    answer: String(ans),
    hint: `Add ${step} each time.`,
  };
};

const makeTwoDigit = () => {
  const op = Math.random() < 0.5 ? '+' : '−';
  if (op === '+') {
    const a = randInt(10, 60);
    const b = randInt(10, 40);
    const ans = a + b;
    return {
      question: `${a} + ${b} = ?`,
      options: buildOptions(ans, [ans + 10, ans - 10, ans + 1]),
      answer: String(ans),
      hint: 'Add the tens, then add the ones.',
    };
  }
  const a = randInt(30, 99);
  const b = randInt(10, a);
  const ans = a - b;
  return {
    question: `${a} − ${b} = ?`,
    options: buildOptions(ans, [ans + 10, ans - 10, ans + 1]),
    answer: String(ans),
    hint: 'Subtract the tens, then subtract the ones.',
  };
};

const makeMultiply = () => {
  const a = randInt(2, 10);
  const b = randInt(2, 10);
  const ans = a * b;
  return {
    question: `${a} × ${b} = ?`,
    options: buildOptions(ans, [ans + a, ans - a, ans + b]),
    answer: String(ans),
    hint: `Think of ${a} groups of ${b}.`,
  };
};

const makeWordProblem = (grade) => {
  const names = ['Mia', 'Kai', 'Zara', 'Amir', 'Noah', 'Ella', 'Diego', 'Aria', 'Leo', 'Priya'];
  const items = [
    { plural: 'apples', ctx: 'basket' },
    { plural: 'stickers', ctx: 'sticker book' },
    { plural: 'crayons', ctx: 'pencil case' },
    { plural: 'seashells', ctx: 'beach bucket' },
    { plural: 'blueberries', ctx: 'bowl' },
    { plural: 'marbles', ctx: 'jar' },
    { plural: 'coins', ctx: 'piggy bank' },
    { plural: 'books', ctx: 'shelf' },
  ];
  const name = names[randInt(0, names.length - 1)];
  const item = items[randInt(0, items.length - 1)];
  // Larger numbers for grade 3+
  const bigger = String(grade || '').match(/^([3-5])$/);
  const isMult = bigger && Math.random() < 0.4;

  if (isMult) {
    const g = randInt(2, 6);
    const p = randInt(2, 6);
    const ans = g * p;
    return {
      question: `${name} has ${g} bags. Each bag has ${p} ${item.plural}. How many ${item.plural} in all?`,
      options: buildOptions(ans, [ans + g, ans - g, ans + p]),
      answer: String(ans),
      hint: `Think of ${g} groups of ${p}.`,
    };
  }

  const op = Math.random() < 0.55 ? '+' : '−';
  if (op === '+') {
    const a = bigger ? randInt(10, 60) : randInt(2, 20);
    const b = bigger ? randInt(10, 40) : randInt(1, 15);
    const ans = a + b;
    return {
      question: `${name} had ${a} ${item.plural} in the ${item.ctx}. A friend gave ${name} ${b} more. How many ${item.plural} does ${name} have now?`,
      options: buildOptions(ans, [ans + 1, ans - 1, ans + 10]),
      answer: String(ans),
      hint: `Add: ${a} + ${b}.`,
    };
  }
  const a = bigger ? randInt(30, 90) : randInt(10, 25);
  const b = bigger ? randInt(5, a - 5) : randInt(1, a - 1);
  const ans = a - b;
  return {
    question: `${name} had ${a} ${item.plural}. ${name} gave ${b} away. How many ${item.plural} are left?`,
    options: buildOptions(ans, [ans + 1, ans - 1, ans + 10].filter((n) => n >= 0)),
    answer: String(ans),
    hint: `Subtract: ${a} − ${b}.`,
  };
};

const buildersBySkill = {
  counting_cardinality: [makeCountingNext.bind(null, 10), makeCountObjects, makeCompare.bind(null, 10)],
  number_recognition:   [makeCountingNext.bind(null, 20), makeCountObjects, makeCompare.bind(null, 20)],
  comparing_numbers:    [makeCompare.bind(null, 20), makeCountingNext.bind(null, 20)],
  addition_within_10:   [makeAdd.bind(null, 10), makeCountingNext.bind(null, 10)],
  subtraction_within_10:[makeSub.bind(null, 10)],
  addition_within_20:   [makeAdd.bind(null, 20), makeSub.bind(null, 20)],
  subtraction_within_20:[makeSub.bind(null, 20), makeAdd.bind(null, 20)],
  place_value:          [makeSkipCount.bind(null, 10), makeCompare.bind(null, 99)],
  two_digit_arithmetic: [makeTwoDigit, makeSkipCount.bind(null, 10), makeSkipCount.bind(null, 5), makeWordProblem.bind(null, '2')],
  skip_counting:        [makeSkipCount.bind(null, 2), makeSkipCount.bind(null, 5), makeSkipCount.bind(null, 10)],
  multiplication:       [makeMultiply, makeSkipCount.bind(null, 3), makeWordProblem.bind(null, '3')],
  multiplication_intro: [makeSkipCount.bind(null, 2), makeSkipCount.bind(null, 5), makeMultiply],
};

const gradeFallbacks = {
  K: [makeCountingNext.bind(null, 10), makeCountObjects, makeCompare.bind(null, 10)],
  '1': [makeAdd.bind(null, 20), makeSub.bind(null, 20), makeCompare.bind(null, 20)],
  '2': [makeTwoDigit, makeSkipCount.bind(null, 10), makeCompare.bind(null, 99), makeWordProblem.bind(null, '2')],
  '3': [makeMultiply, makeTwoDigit, makeSkipCount.bind(null, 5), makeWordProblem.bind(null, '3')],
  '4': [makeMultiply, makeTwoDigit, makeWordProblem.bind(null, '4')],
  '5': [makeMultiply, makeTwoDigit, makeWordProblem.bind(null, '5')],
};

export const generateKmathQuestions = (activity, count = 5) => {
  const content = (activity && activity.content) || {};
  const skill = String(content.skill || '').toLowerCase();
  const grade = String(content.grade || '').toUpperCase();
  const pool = buildersBySkill[skill] || gradeFallbacks[grade] || gradeFallbacks.K;

  const questions = [];
  const seen = new Set();
  let safety = count * 6;
  while (questions.length < count && safety-- > 0) {
    const builder = pool[randInt(0, pool.length - 1)];
    const q = builder();
    const key = q.question;
    if (seen.has(key)) continue;
    seen.add(key);
    questions.push(q);
  }
  return questions;
};

export const isKmathActivity = (activityId) => String(activityId || '').startsWith('kmath-');
